import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { category: '', problem: '', solution: '', module: '', keywords: '' };

const T = {
  loading: { en: 'Loading Knowledge Base…', hi: 'नॉलेज बेस लोड हो रहा है…' },
  title: { en: 'Aayush Knowledge Base', hi: 'आयुष नॉलेज बेस' },
  subtitle_a: { en: 'Aayush answers from these problem→solution pairs (ask in any style). Changes go ', hi: 'आयुष इन्हीं problem→solution से जवाब देता है (किसी भी अंदाज़ में पूछें)। बदलाव ' },
  live_now: { en: 'live instantly', hi: 'तुरंत live' },
  no_deploy: { en: ' — no deploy needed.', hi: ' — कोई deploy नहीं।' },
  entries: { en: 'entries', hi: 'entries' },
  bulk_import: { en: '⬆ Bulk Import', hi: '⬆ बल्क आयात' },
  add_new: { en: '+ Add New', hi: '+ नया जोड़ें' },
  search_ph: { en: '🔍 Search (problem / module / keyword)…', hi: '🔍 खोजें (problem / module / keyword)…' },
  all_categories: { en: 'All Categories', hi: 'सभी Categories' },
  new_entry: { en: 'New entry', hi: 'नई entry' },
  edit_entry: { en: 'Edit entry', hi: 'entry edit करें' },
  cat_ph: { en: 'Category', hi: 'Category' },
  module_ph: { en: 'Module / Feature name', hi: 'Module / Feature नाम' },
  problem_ph: { en: "Problem (school owner's issue)…", hi: 'Problem (school owner की समस्या)…' },
  solution_ph: { en: 'Solution (Aayush answers from this)…', hi: 'Solution (Aayush इसी से जवाब देगा)…' },
  keywords_ph: { en: 'Keywords / synonyms (to improve matching, comma-separated)', hi: 'Keywords / synonyms (matching बेहतर करने के लिए, comma-separated)' },
  saving: { en: 'Saving…', hi: 'सेव…' },
  save_live: { en: '💾 Save & Go Live', hi: '💾 Save & Go Live' },
  cancel: { en: 'Cancel', hi: 'रद्द करें' },
  no_entry: { en: 'No entry found.', hi: 'कोई entry नहीं मिली।' },
  edit: { en: '✏️ Edit', hi: '✏️ Edit' },
  del: { en: '🗑 Delete', hi: '🗑 Delete' },
  import_title: { en: 'Bulk Import (JSON array)', hi: 'बल्क आयात (JSON array)' },
  cancel_short: { en: 'Cancel', hi: 'रद्द' },
  import: { en: 'Import', hi: 'आयात' },
  need_ps: { en: 'Problem and Solution are required', hi: 'Problem और Solution ज़रूरी हैं' },
  added_live: { en: '✅ Added — live!', hi: '✅ जोड़ा गया — live!' },
  updated_live: { en: '✅ Updated — live!', hi: '✅ अपडेट हुआ — live!' },
  save_failed: { en: 'Save failed', hi: 'Save failed' },
  confirm_del: { en: 'Delete this entry?', hi: 'यह entry हटाएँ?' },
  deleted: { en: 'Deleted', hi: 'हटा दिया' },
  del_failed: { en: 'Delete failed', hi: 'Delete failed' },
  invalid_json: { en: 'Invalid JSON', hi: 'अमान्य JSON' },
  must_array: { en: 'JSON must be an array', hi: 'JSON एक array होना चाहिए' },
  import_failed: { en: 'Import failed', hi: 'Import failed' },
};

export default function AayushKnowledgeBase({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (T[k] ? T[k][language] || T[k].en : k);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [editing, setEditing] = useState(null); // entry id or 'new'
  const [form, setForm] = useState(EMPTY);
  const [toast, setToast] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/knowledge-base`, { headers });
      const data = await res.json();
      if (data.success) { setEntries(data.entries); setCategories(data.categories); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (activeCat !== 'all' && e.category !== activeCat) return false;
      if (!q) return true;
      return `${e.problem} ${e.solution} ${e.module} ${e.keywords} ${e.category}`.toLowerCase().includes(q);
    });
  }, [entries, search, activeCat]);

  const startNew = () => { setForm({ ...EMPTY, category: activeCat !== 'all' ? activeCat : '' }); setEditing('new'); };
  const startEdit = (e) => { setForm({ category: e.category, problem: e.problem, solution: e.solution, module: e.module, keywords: e.keywords || '' }); setEditing(e.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.problem.trim() || !form.solution.trim()) { showToast(tr('need_ps')); return; }
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const res = await fetch(`${API_URL}/api/live-chat/admin/knowledge-base${isNew ? '' : '/' + editing}`, {
        method: isNew ? 'POST' : 'PUT', headers, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { showToast(isNew ? tr('added_live') : tr('updated_live')); cancel(); load(); }
      else showToast(data.detail || tr('save_failed'));
    } catch (e) { showToast(tr('save_failed')); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm(tr('confirm_del'))) return;
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/knowledge-base/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) { showToast(tr('deleted')); load(); }
    } catch (e) { showToast(tr('del_failed')); }
  };

  const doImport = async () => {
    let parsed;
    try { parsed = JSON.parse(importText); } catch { showToast(tr('invalid_json')); return; }
    if (!Array.isArray(parsed)) { showToast(tr('must_array')); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/knowledge-base/bulk-import`, {
        method: 'POST', headers, body: JSON.stringify({ entries: parsed }),
      });
      const data = await res.json();
      if (data.success) { showToast(`✅ ${data.imported} ${tr('entries')} import`); setShowImport(false); setImportText(''); load(); }
    } catch (e) { showToast(tr('import_failed')); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-gray-500" data-testid="kb-loading">{tr('loading')}</div>;

  return (
    <div className="max-w-6xl mx-auto" data-testid="aayush-kb-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><span>🧠</span> {tr('title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{tr('subtitle_a')}<b>{tr('live_now')}</b>{tr('no_deploy')} <span className="text-emerald-600">{entries.length} {tr('entries')}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="kb-import-btn">{tr('bulk_import')}</button>
          <button onClick={startNew} className="px-5 py-2 text-sm font-bold rounded-lg bg-orange-500 text-white hover:bg-orange-600" data-testid="kb-add-btn">{tr('add_new')}</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr('search_ph')} className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none" data-testid="kb-search" />
        <select value={activeCat} onChange={(e) => setActiveCat(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="kb-category-filter">
          <option value="all">{tr('all_categories')}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {(editing) && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-4" data-testid="kb-edit-form">
          <h3 className="font-semibold text-slate-800 mb-3">{editing === 'new' ? tr('new_entry') : tr('edit_entry')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={tr('cat_ph')} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="kb-form-category" list="kb-cats" />
            <datalist id="kb-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            <input value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} placeholder={tr('module_ph')} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="kb-form-module" />
          </div>
          <textarea value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} placeholder={tr('problem_ph')} className="w-full mt-3 px-3 py-2 text-sm border border-gray-200 rounded-lg h-20" data-testid="kb-form-problem" />
          <textarea value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} placeholder={tr('solution_ph')} className="w-full mt-3 px-3 py-2 text-sm border border-gray-200 rounded-lg h-24" data-testid="kb-form-solution" />
          <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder={tr('keywords_ph')} className="w-full mt-3 px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="kb-form-keywords" />
          <div className="flex gap-2 mt-3">
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-bold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50" data-testid="kb-form-save">{saving ? tr('saving') : tr('save_live')}</button>
            <button onClick={cancel} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600" data-testid="kb-form-cancel">{tr('cancel')}</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="kb-list">
        {filtered.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">{tr('no_entry')}</p>}
        {filtered.map((e) => (
          <div key={e.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-orange-300 transition" data-testid={`kb-entry-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 mb-1">{e.category}</span>
                <p className="text-sm font-semibold text-slate-800">{e.problem}</p>
                <p className="text-sm text-gray-600 mt-1">{e.solution}</p>
                {e.module && <p className="text-xs text-orange-600 mt-1 font-medium">🔧 {e.module}</p>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => startEdit(e)} className="text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50" data-testid={`kb-edit-${e.id}`}>{tr('edit')}</button>
                <button onClick={() => remove(e.id)} className="text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50" data-testid={`kb-delete-${e.id}`}>{tr('del')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showImport && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="kb-import-modal">
          <div className="bg-white rounded-2xl p-5 w-full max-w-lg">
            <h3 className="font-bold text-slate-800 mb-2">{tr('import_title')}</h3>
            <p className="text-xs text-gray-500 mb-2">{`[{"category":"Fees","problem":"...","solution":"...","module":"...","keywords":"..."}, ...]`}</p>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} className="w-full h-48 font-mono text-xs border border-gray-200 rounded-lg p-2" data-testid="kb-import-textarea" />
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => setShowImport(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">{tr('cancel_short')}</button>
              <button onClick={doImport} disabled={saving} className="px-5 py-2 text-sm font-bold rounded-lg bg-orange-500 text-white disabled:opacity-50" data-testid="kb-import-submit">{tr('import')}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-2 rounded-xl text-sm shadow-lg z-[60]" data-testid="kb-toast">{toast}</div>}
    </div>
  );
}
